<template>
  <div class="space-y-6">
    <!-- En-tête avec bouton d'ajout -->
    <div class="flex justify-between items-center">
      <div>
        <h2 class="text-2xl font-extrabold text-blue-900 dark:text-gray-100">Gestion des Permissions</h2>
        <p class="text-gray-600 dark:text-gray-400 mt-1">Organisez et gérez les permissions du système</p>
      </div>
      <button 
        @click="openModal"
        class="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-all duration-200 flex items-center shadow-sm hover:shadow-md"
      >
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
        Ajouter une Permission
      </button>
    </div>

    <!-- Tableau des permissions -->
    <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-gray-700 dark:to-gray-600">
            <tr>
              <th class="px-6 py-4 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                <div class="flex items-center space-x-1">
                  <span>Nom</span>
                </div>
              </th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                Type
              </th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                <div class="flex items-center space-x-1">
                  <span>Date de création</span>
                </div>
              </th>
              <th class="px-6 py-4 text-right text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-600">
            <tr 
              v-for="permission in paginatedPermissions" 
              :key="permission.id" 
              class="hover:bg-blue-50 dark:hover:bg-gray-700/50 transition-colors duration-150 group"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div>
                    <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ permission.name }}</div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4">
                <div class="text-sm">
                  <span 
                    :class="[
                      'px-2 py-1 rounded-full text-xs font-medium',
                      isSystemPermission(permission.name) 
                        ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' 
                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    ]"
                  >
                    {{ isSystemPermission(permission.name) ? 'Système' : 'Personnalisée' }}
                  </span>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center space-x-2">
                  <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  <div class="text-sm text-gray-500 dark:text-gray-400 font-medium">{{ formatDate(permission.created_at) }}</div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div class="flex justify-end space-x-1">
                  <button 
                    v-if="!isSystemPermission(permission.name)"
                    @click="editPermission(permission)"
                    class="p-2 text-blue-600 dark:text-blue-400 hover:text-white hover:bg-blue-600 dark:hover:bg-blue-500 rounded-lg transition-all duration-200 group/btn"
                    title="Modifier"
                  >
                    <svg class="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                    </svg>
                  </button>
                  <button 
                    v-if="!isSystemPermission(permission.name)"
                    @click="deletePermission(permission.id)"
                    class="p-2 text-red-600 dark:text-red-400 hover:text-white hover:bg-red-600 dark:hover:bg-red-500 rounded-lg transition-all duration-200 group/btn"
                    title="Supprimer"
                  >
                    <svg class="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                    </svg>
                  </button>
                  <span 
                    v-if="isSystemPermission(permission.name)"
                    class="text-xs text-gray-400 dark:text-gray-500 italic"
                  >
                    Lecture seule
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- État de chargement -->
      <div v-if="loading" class="p-8 text-center">
        <div class="inline-flex items-center space-x-3">
          <svg class="animate-spin h-6 w-6 text-blue-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span class="text-gray-600 dark:text-gray-400 font-medium">Chargement des permissions...</span>
        </div>
      </div>

      <!-- Message vide -->
      <div v-if="!loading && permissions.length === 0" class="p-12 text-center">
        <div class="max-w-sm mx-auto">
          <div class="h-20 w-20 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="h-10 w-10 text-blue-900 dark:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Aucune permission trouvée</h3>
          <p class="text-gray-500 dark:text-gray-400 mb-4">Commencez par créer votre première permission.</p>
          <button 
            @click="openModal"
            class="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors inline-flex items-center"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            Créer une permission
          </button>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && permissions.length > 0" class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
        <div class="flex items-center justify-between">
          <!-- Informations de pagination -->
          <div class="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
            <span>Total: </span>
            <span class="font-semibold text-blue-900 dark:text-blue-300">{{ totalItems }}</span>
            <span>permissions</span>
          </div>

          <!-- Contrôles de pagination -->
          <div class="flex items-center space-x-2">
            <!-- Sélecteur d'éléments par page -->
            <div class="flex items-center space-x-2">
              <span class="text-sm text-gray-700 dark:text-gray-300">Afficher :</span>
              <select 
                v-model="itemsPerPage" 
                @change="currentPage = 1"
                class="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-900"
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
            </div>

            <!-- Boutons de navigation -->
            <div class="flex items-center space-x-1">
              <button
                @click="currentPage = 1"
                :disabled="currentPage === 1"
                class="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Première page"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
                </svg>
              </button>
              
              <button
                @click="currentPage--"
                :disabled="currentPage === 1"
                class="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Page précédente"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
              </button>

              <!-- Numéros de page -->
              <div class="flex items-center space-x-1">
                <button
                  v-for="page in visiblePages"
                  :key="page"
                  @click="currentPage = page"
                  :class="[
                    'px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200',
                    currentPage === page 
                      ? 'bg-blue-900 text-white shadow-sm' 
                      : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  ]"
                >
                  {{ page }}
                </button>
                
                <span v-if="hasMorePages" class="px-2 text-gray-500 dark:text-gray-400">...</span>
              </div>

              <button
                @click="currentPage++"
                :disabled="currentPage === totalPages"
                class="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Page suivante"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>
              
              <button
                @click="currentPage = totalPages"
                :disabled="currentPage === totalPages"
                class="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Dernière page"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal pour ajouter/modifier une permission -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-lg w-full max-w-md transform transition-all duration-300 scale-95 hover:scale-100">
        <div class="p-6">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            {{ isEditing ? 'Modifier la Permission' : 'Ajouter une Permission' }}
          </h3>
          
          <form @submit.prevent="submitForm">
            <div class="space-y-4">
              <div>
                <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nom de la permission *
                </label>
                <input
                  type="text"
                  id="name"
                  v-model="form.name"
                  required
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                  placeholder="Ex: users-create, posts-delete"
                  :disabled="submitting"
                >
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Format recommandé: resource-action (ex: users-view, posts-edit)
                </p>
              </div>
            </div>
            
            <div class="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                @click="closeModal"
                class="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors disabled:opacity-50"
                :disabled="submitting"
              >
                Annuler
              </button>
              <button
                type="submit"
                class="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors disabled:opacity-50 flex items-center shadow-sm hover:shadow-md"
                :disabled="submitting"
              >
                <svg v-if="submitting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ submitting ? 'Traitement...' : (isEditing ? 'Modifier' : 'Ajouter') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PermissionsContent',
  data() {
    return {
      permissions: [],
      loading: false,
      submitting: false,
      showModal: false,
      isEditing: false,
      currentPermissionId: null,
      form: {
        name: ''
      },
      // Pagination
      currentPage: 1,
      itemsPerPage: 10,
      // Permissions système (doivent correspondre à celles dans votre contrôleur)
      systemPermissions: [
        'users-all',
        'users-view',
        'users-create',
        'users-edit',
        'users-delete',
        'roles-all',
        'roles-view',
        'roles-create',
        'roles-edit',
        'roles-delete',
        'permissions-all',
        'permissions-view',
        'permissions-create',
        'permissions-edit',
        'permissions-delete',
      ]
    };
  },
  computed: {
    // Calcul des éléments paginés
    paginatedPermissions() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.permissions.slice(start, end);
    },
    
    // Informations de pagination
    totalItems() {
      return this.permissions.length;
    },
    
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.totalItems);
    },
    
    // Pages visibles pour la pagination
    visiblePages() {
      const pages = [];
      const maxVisiblePages = 5;
      
      let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);
      
      // Ajuster si on est près de la fin
      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      return pages;
    },
    
    hasMorePages() {
      return this.visiblePages[this.visiblePages.length - 1] < this.totalPages;
    }
  },
  watch: {
    // Réinitialiser à la première page quand les données changent
    permissions() {
      this.currentPage = 1;
    }
  },
  mounted() {
    this.fetchPermissions();
  },
  methods: {
    async fetchPermissions() {
      this.loading = true;
      try {
        const response = await fetch('/permissions', {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          this.permissions = data || [];
        } else {
          console.error('Erreur lors du chargement des permissions');
          this.showError('Erreur lors du chargement des permissions');
        }
      } catch (error) {
        console.error('Erreur:', error);
        this.showError('Erreur de connexion');
      } finally {
        this.loading = false;
      }
    },

    isSystemPermission(permissionName) {
      return this.systemPermissions.includes(permissionName);
    },

    openModal() {
      this.isEditing = false;
      this.currentPermissionId = null;
      this.form = { name: '' };
      this.showModal = true;
    },

    editPermission(permission) {
      if (this.isSystemPermission(permission.name)) {
        this.showError('Les permissions système ne peuvent pas être modifiées');
        return;
      }

      this.isEditing = true;
      this.currentPermissionId = permission.id;
      this.form = {
        name: permission.name
      };
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.isEditing = false;
      this.currentPermissionId = null;
      this.form = { name: '' };
      this.submitting = false;
    },

    async submitForm() {
      this.submitting = true;
      try {
        const url = this.isEditing ? `/permissions/${this.currentPermissionId}` : '/permissions';
        const method = this.isEditing ? 'PUT' : 'POST';

        // Récupération du token CSRF depuis les meta tags
        const token = this.getCsrfToken();

        const response = await fetch(url, {
          method: method,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-TOKEN': token,
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(this.form)
        });

        const data = await response.json();

        if (response.ok) {
          this.showSuccess(this.isEditing ? 'Permission modifiée avec succès' : 'Permission créée avec succès');
          this.closeModal();
          this.fetchPermissions();
        } else {
          // Gestion des erreurs de validation
          if (data.errors) {
            const errorMessages = Object.values(data.errors).flat().join(', ');
            this.showError('Erreur de validation: ' + errorMessages);
          } else {
            this.showError(data.message || 'Une erreur est survenue');
          }
        }
      } catch (error) {
        console.error('Erreur:', error);
        this.showError('Erreur de connexion au serveur');
      } finally {
        this.submitting = false;
      }
    },

    async deletePermission(id) {
      const permission = this.permissions.find(p => p.id === id);
      if (permission && this.isSystemPermission(permission.name)) {
        this.showError('Les permissions système ne peuvent pas être supprimées');
        return;
      }

      if (confirm('Êtes-vous sûr de vouloir supprimer cette permission ?')) {
        try {
          const token = this.getCsrfToken();
          
          const response = await fetch(`/permissions/${id}`, {
            method: 'DELETE',
            headers: {
              'X-CSRF-TOKEN': token,
              'X-Requested-With': 'XMLHttpRequest'
            }
          });

          if (response.ok) {
            this.showSuccess('Permission supprimée avec succès');
            this.fetchPermissions();
          } else {
            const data = await response.json();
            this.showError(data.message || 'Erreur lors de la suppression');
          }
        } catch (error) {
          console.error('Erreur:', error);
          this.showError('Erreur lors de la suppression');
        }
      }
    },

    getCsrfToken() {
      // Méthode 1: Récupérer depuis la meta tag
      const metaTag = document.querySelector('meta[name="csrf-token"]');
      if (metaTag) {
        return metaTag.getAttribute('content');
      }

      // Méthode 2: Récupérer depuis les cookies
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      if (cookieValue) {
        return decodeURIComponent(cookieValue);
      }

      // Méthode 3: Utiliser le token fourni par Laravel
      if (window.Laravel && window.Laravel.csrfToken) {
        return window.Laravel.csrfToken;
      }

      console.warn('CSRF token non trouvé');
      return '';
    },

    showSuccess(message) {
      // Vous pouvez utiliser une librairie de notifications ou un simple alert
      alert('Succès: ' + message);
      // Ou implémenter un système de toast
      // this.$toast.success(message);
    },

    showError(message) {
      alert('Erreur: ' + message);
      // Ou implémenter un système de toast
      // this.$toast.error(message);
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  }
};
</script>